import { useMemo, useState } from "react"
import { Button, Calendar, DatePopover, Label, PopoverContent, PopoverTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { Matcher } from "react-day-picker"

interface DateInputProps {
  id: string
  value?: Date
  onChange?: (date?: Date) => void
  label?: string
  placeholder?: string
  disabled?: boolean
  error?: boolean
  defaultMonth?: Date
  minDate?: Date
  maxDate?: Date
}

export function DateInput({
  value,
  onChange,
  label,
  placeholder = "Pick a date",
  disabled = false,
  error,
  id,
  defaultMonth,
  minDate,
  maxDate,
}: DateInputProps) {
  const [open, setOpen] = useState(false)

  const disabledDates = useMemo<Matcher | Matcher[] | undefined>(() => {
    const matchers: Matcher[] = []
    if (minDate) matchers.push({ before: minDate })
    if (maxDate) matchers.push({ after: maxDate })
    return matchers.length > 0 ? matchers : undefined
  }, [minDate, maxDate])

  return (
    <div className="space-y-1">
      {label && (
        <Label htmlFor={id} className={cn(error && "text-destructive")}>
          {label}
        </Label>
      )}
      <DatePopover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            disabled={disabled}
            className={cn("w-full justify-start text-left font-normal", !value && "text-muted-foreground")}
          >
            <CalendarIcon />
            {value ? format(value, "MMM dd, yyyy") : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={(date) => {
              onChange?.(date)
              setOpen(false)
            }}
            disabled={disabledDates}
            defaultMonth={defaultMonth || value || new Date()}
            initialFocus
          />
        </PopoverContent>
      </DatePopover>
    </div>
  )
}
